// **The slide deck's registry** (`demo-presentation-shell` task 4.2). Maps a `SlideId` (`cues.ts`)
// to the component that renders it, and exposes `renderSlide` — the one seam the rest of the
// stage layer needs. `slide-vibe-coding-quote` and `slide-quote-return` deliberately map to the
// same `QuoteSlide` component (see that file's header): it is one slide the cue list revisits, not
// two components with duplicated content.
//
// `SHIPPED_SLIDE_IDS` is derived from `cues.ts`'s `CUE_LIST` (deduplicated, first-occurrence
// order) rather than hand-listed a second time — the cue list is already the single source of
// presentation order (design DD2); restating the id set here would be the "second ordering"
// DD2 exists to rule out.

import type { ComponentType, ReactElement } from 'react';
import { CUE_LIST } from '../cues';
import { TitleSlide } from './TitleSlide';
import { QuoteSlide } from './QuoteSlide';
import { ApiophobiaSlide } from './ApiophobiaSlide';
import { TranslationChartSlide } from './TranslationChartSlide';
import { LoveSlide } from './LoveSlide';
import { ProgrammingChartSlide } from './ProgrammingChartSlide';
import { SeeSayRunSlide } from './SeeSayRunSlide';
import { ClosingSlide } from './ClosingSlide';
import { SunsetSlide } from './SunsetSlide';
import { StarterPackSlide } from './StarterPackSlide';
import { FinisherSlide } from './FinisherSlide';
import { PvpIntroductionSlide } from './PvpIntroductionSlide';

export interface SlideProps {
  /** How many `reveal` cues have applied since this slide was shown (`sequencer.ts`'s
   * `revealStep`). Slides with no progressive reveal ignore it. */
  revealStep: number;
}

const SLIDE_REGISTRY: Record<string, ComponentType<SlideProps>> = {
  'slide-cover': TitleSlide,
  'slide-vibe-coding-quote': QuoteSlide,
  'slide-quote-return': QuoteSlide,
  'slide-apiophobia': ApiophobiaSlide,
  'slide-translation-vibes-chart': TranslationChartSlide,
  'slide-love-sentences': LoveSlide,
  // The script interrupts the chart with the love example and then brings it back "now with German
  // added" — the same slide, re-entered one step further on. Mounting the one component at a base
  // step is what keeps the chart's three states in one place; a second component with the same
  // states copied into it is the drift this avoids.
  'slide-translation-chart-german': ({ revealStep }: SlideProps) => (
    <TranslationChartSlide revealStep={revealStep + 1} />
  ),
  'slide-programming-as-translation': ProgrammingChartSlide,
  'slide-see-say-run': SeeSayRunSlide,
  'slide-pvp-introduction': PvpIntroductionSlide,
  'slide-closing': ClosingSlide,
  'slide-sunset': SunsetSlide,
  'slide-starter-pack': StarterPackSlide,
  // Registered but not yet cued (presentation polish, 2026-08-11): the finisher's cue lands with the
  // rest of the reveal-count rewiring in `cues.ts`, which is edited in one serialised step because it
  // is the registry every parallel track would otherwise collide in. `SHIPPED_SLIDE_IDS` derives from
  // the cue list, so an uncued registration simply is not shown yet — it is not a dead branch.
  'slide-finisher': FinisherSlide,
};

/** Every slide id the shipped cue list actually shows, in the order it first shows them. */
export const SHIPPED_SLIDE_IDS: readonly string[] = (() => {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const cue of CUE_LIST) {
    if (cue.kind === 'slide' && !seen.has(cue.slide)) {
      seen.add(cue.slide);
      ids.push(cue.slide);
    }
  }
  return ids;
})();

/** Renders the given slide id at the given reveal step. Fails loudly on an id with no registered
 * component — a defaulted "blank slide" is exactly the failure that draws successfully and is
 * wrong (the `DD4` precedent), and on stage it is silent. */
export function renderSlide(slideId: string, revealStep: number): ReactElement {
  const Component = SLIDE_REGISTRY[slideId];
  if (Component === undefined) {
    throw new Error(`no slide component registered for slide id "${slideId}"`);
  }
  // Keyed by slide id so the deck's entrance animation (`slides.css`) actually replays when one
  // slide replaces another that happens to render through the same component — the quote slide,
  // shown twice, and the translation chart's two entrances.
  return <Component key={slideId} revealStep={revealStep} />;
}
